/**
 * 1.8.4 (D-1.8.4-6) — the reports of Past events: one event's, and the last twelve months'. Counts only — never a
 * tenant's or a volunteer's name (the lists stay on each event's page).
 *
 * Only the numbers that applied, by the same switches as `shared/event-cards.ts`:
 *
 *   RSVP on        invited (everyone with a line, or the invitation's count when larger), said yes, came (lines
 *                  checked in at the door), households, guests
 *   RSVP off       none of those — "On the calendar only — nobody answered, so there are no counts."
 *   Volunteers     when the event asked for some (Volunteers needed above 0): how many held a place, of how many;
 *                  an RSVP event volunteers answered without a number: how many held a place
 *   Raffle         the places, the guaranteed, how many were drawn from how many, the waiting list
 *   Hours          volunteer hours are kept for volunteer visits and activities (the hours ledger), never for an
 *                  event — the report says so in words rather than print a number nobody recorded.
 *
 * The last twelve months are the twelve calendar months ending with today's (the window of "Events in charts"): every
 * past event in a row, newest first; the totals add what applied, and a cancelled event is listed and never counted.
 *
 * Pure: it reads the `EventView` the Events page already has (and the server's `listEvents` gives) and writes nothing.
 * The words of the copied text and of the PDF are here (English and Spanish); the screen's are the i18n keys `ev184.*`.
 */
import { addDaysToKey } from './dates';
import type { EventsListWords } from './event-charts';
import type { EventView } from './events';
import { minutesLabel } from './volunteers';

export type ReportLang = 'en' | 'es';
/** How people got a place — or nobody answered at all. */
export type ReportShape = 'calendar' | 'everyone' | 'first' | 'raffle';
export const REPORT_COUNTS = ['invited', 'saidYes', 'came', 'households', 'guests'] as const;
export type ReportCount = (typeof REPORT_COUNTS)[number];

export interface EventReport {
  eventId: string;
  name: string;
  dayKey: string;
  minutes: number | null;
  endMinutes: number | null;
  place: string;
  /** The kind the office gave it ("Tech discussion"); '' when none. */
  kind: string;
  shape: ReportShape;
  cancelled: boolean;
  /** The RSVP counts — null when RSVP was off (nothing was counted). */
  counts: Record<ReportCount, number> | null;
  /** Volunteers who held a place, and how many were asked for (null: no number was set). Null when no volunteer applied. */
  volunteers: { going: number; needed: number | null } | null;
  /** The raffle's result (null: not a raffle). */
  raffle: { drawn: boolean; places: number | null; pool: number; guaranteed: number; winners: number; waiting: number } | null;
}

export interface YearTotals {
  /** Events held (cancelled ones are not). */
  events: number;
  cancelled: number;
  invited: number;
  saidYes: number;
  came: number;
  households: number;
  guests: number;
  volunteers: number;
}

export interface YearReport {
  fromKey: string;
  toKey: string;
  /** Newest first. */
  rows: EventReport[];
  totals: YearTotals;
}

/** One event's report: only what applied. */
export function eventReport(e: EventView): EventReport {
  const rsvp = e.cal.rsvp;
  const shape: ReportShape = !rsvp ? 'calendar' : e.rule === 'raffle' ? 'raffle' : e.rule === 'first' ? 'first' : 'everyone';
  const needed = e.numbers.volunteerPlaces ?? 0;
  const volunteers = needed > 0 ? { going: e.three.places.volunteersGoing, needed } : rsvp && e.counts.volunteers > 0 ? { going: e.counts.volunteers, needed: null } : null;
  const p = e.three.places;
  return {
    eventId: e.eventId,
    name: e.name,
    dayKey: e.dayKey,
    minutes: e.minutes,
    endMinutes: e.endMinutes,
    place: e.place,
    kind: e.cal.kind,
    shape,
    cancelled: e.status === 'Cancelled',
    counts: rsvp ? { invited: Math.max(e.counts.asked, e.invited.count), saidYes: e.counts.saidYes, came: e.counts.checkedIn, households: e.counts.households, guests: e.counts.guests } : null,
    volunteers,
    raffle: shape === 'raffle' ? { drawn: !!e.raffle?.drawnAt, places: e.numbers.tenantPlaces ?? p.tenantPlaces, pool: p.drawnFrom ?? e.raffle?.poolSize ?? 0, guaranteed: p.guaranteed, winners: p.winners, waiting: p.waiting } : null,
  };
}

/** The first day of the twelve months ending with today's ("20251101" for any day of October 2026). */
export function yearWindowStart(todayKey: string): string {
  let m = `${todayKey.slice(0, 6)}01`;
  for (let i = 0; i < 11; i++) m = `${addDaysToKey(m, -1).slice(0, 6)}01`;
  return m;
}

const isPast = (e: Pick<EventView, 'past' | 'dayKey'>, todayKey: string) => e.past || e.dayKey < todayKey;

/** The last twelve months: every past event in a row (newest first) and the totals of what applied. */
export function yearReport(events: readonly EventView[], todayKey: string): YearReport {
  const fromKey = yearWindowStart(todayKey);
  const rows = events
    .filter((e) => isPast(e, todayKey) && e.dayKey >= fromKey && e.dayKey <= todayKey)
    .sort((a, b) => b.dayKey.localeCompare(a.dayKey) || (b.minutes ?? 0) - (a.minutes ?? 0))
    .map(eventReport);
  const totals: YearTotals = { events: 0, cancelled: 0, invited: 0, saidYes: 0, came: 0, households: 0, guests: 0, volunteers: 0 };
  for (const r of rows) {
    if (r.cancelled) {
      totals.cancelled++;
      continue;
    }
    totals.events++;
    if (r.counts) for (const k of REPORT_COUNTS) totals[k] += r.counts[k];
    if (r.volunteers) totals.volunteers += r.volunteers.going;
  }
  return { fromKey, toKey: todayKey, rows, totals };
}

/* ------------------------------------------------------------------------------------------------
 * Words (the copied text and the PDF)
 * ---------------------------------------------------------------------------------------------- */

export interface ReportWords {
  title: string;
  yearTitle: string;
  date: string;
  time: string;
  place: string;
  kind: string;
  shape: Record<ReportShape, string>;
  invited: string;
  saidYes: string;
  came: string;
  households: string;
  guests: string;
  volunteers: string;
  /** "{going} of {needed}" */
  of: string;
  volunteerHours: string;
  hoursNotKept: string;
  raffle: string;
  /** "{places} places · {guaranteed} guaranteed · {winners} drawn from {pool} · {waiting} waiting" */
  raffleDrawn: string;
  raffleNotDrawn: string;
  noLimit: string;
  calendarOnly: string;
  cancelled: string;
  /** "{from} to {to}" */
  window: string;
  totals: string;
  events: string;
  eventsCancelled: string;
  none: string;
  countsOnly: string;
}

export const REPORT_WORDS: Record<ReportLang, ReportWords> = {
  en: {
    title: 'Event report',
    yearTitle: 'Events — the last 12 months',
    date: 'Date',
    time: 'Time',
    place: 'Place',
    kind: 'Kind',
    shape: { calendar: 'On the calendar only', everyone: 'RSVP — everyone who answers', first: 'RSVP — first come, first served', raffle: 'RSVP — raffle' },
    invited: 'Invited',
    saidYes: 'Said yes',
    came: 'Came (checked in)',
    households: 'Households',
    guests: 'Guests',
    volunteers: 'Volunteers',
    of: '{going} of {needed}',
    volunteerHours: 'Volunteer hours',
    hoursNotKept: 'Volunteer hours: not recorded for events — hours are kept for volunteer visits and activities.',
    raffle: 'Raffle',
    raffleDrawn: '{places} places · {guaranteed} guaranteed · {winners} drawn from {pool} · {waiting} waiting',
    raffleNotDrawn: 'Not drawn',
    noLimit: 'no limit',
    calendarOnly: 'On the calendar only — nobody answered, so there are no counts.',
    cancelled: 'Cancelled — not counted in the totals.',
    window: '{from} to {to}',
    totals: 'Totals',
    events: 'Events held',
    eventsCancelled: 'Cancelled',
    none: 'No past events in these twelve months.',
    countsOnly: 'Counts only — no names. The lists are on each event’s page.',
  },
  es: {
    title: 'Informe del evento',
    yearTitle: 'Eventos — los últimos 12 meses',
    date: 'Fecha',
    time: 'Hora',
    place: 'Lugar',
    kind: 'Tipo',
    shape: { calendar: 'Solo en el calendario', everyone: 'RSVP — todos los que respondan', first: 'RSVP — por orden de llegada', raffle: 'RSVP — rifa' },
    invited: 'Invitados',
    saidYes: 'Dijeron que sí',
    came: 'Vinieron (registrados)',
    households: 'Hogares',
    guests: 'Invitados de los inquilinos',
    volunteers: 'Voluntarios',
    of: '{going} de {needed}',
    volunteerHours: 'Horas de voluntariado',
    hoursNotKept: 'Horas de voluntariado: no se registran para los eventos — las horas se guardan para las visitas y las actividades de voluntarios.',
    raffle: 'Rifa',
    raffleDrawn: '{places} lugares · {guaranteed} garantizados · {winners} sorteados entre {pool} · {waiting} en espera',
    raffleNotDrawn: 'Sin sortear',
    noLimit: 'sin límite',
    calendarOnly: 'Solo en el calendario — nadie respondió, así que no hay números.',
    cancelled: 'Cancelado — no se cuenta en los totales.',
    window: '{from} a {to}',
    totals: 'Totales',
    events: 'Eventos realizados',
    eventsCancelled: 'Cancelados',
    none: 'No hay eventos pasados en estos doce meses.',
    countsOnly: 'Solo números — sin nombres. Las listas están en la página de cada evento.',
  },
};

/** The Spanish words of the two attendance charts (the same as `num175.events` in es.json; a test holds them equal). */
export const EVENTS_LIST_WORDS_ES: EventsListWords = {
  byEvent: { title: 'Asistencia por evento', note: 'Personas con lugar y personas registradas en la puerta.', category: 'Evento', expected: 'Esperadas', came: 'Registradas' },
  trend: { title: 'Los últimos doce meses', note: 'Personas con lugar y personas registradas, mes por mes.', category: 'Mes', expected: 'Esperadas', came: 'Registradas' },
  unit: 'personas',
};

const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k: string) => String(v[k] ?? ''));
const civil = (dayKey: string) => new Date(Date.UTC(Number(dayKey.slice(0, 4)), Number(dayKey.slice(4, 6)) - 1, Number(dayKey.slice(6, 8)), 12));
/** "Friday, September 18, 2026" / "viernes, 18 de septiembre de 2026". */
export const reportDay = (dayKey: string, lang: ReportLang) => new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(civil(dayKey));
/** "2:00 pm – 3:00 pm", or '' when the event has no time. */
export const reportTime = (r: Pick<EventReport, 'minutes' | 'endMinutes'>) => (r.minutes === null ? '' : r.endMinutes !== null && r.endMinutes > r.minutes ? `${minutesLabel(r.minutes)} – ${minutesLabel(r.endMinutes)}` : minutesLabel(r.minutes));
/** "Tech discussion · On the calendar only". */
export const reportKind = (r: Pick<EventReport, 'kind' | 'shape'>, lang: ReportLang) => [r.kind, REPORT_WORDS[lang].shape[r.shape]].filter(Boolean).join(' · ');
/** The raffle's result in words. */
export function raffleWords(x: NonNullable<EventReport['raffle']>, lang: ReportLang): string {
  const w = REPORT_WORDS[lang];
  if (!x.drawn) return w.raffleNotDrawn;
  return fill(w.raffleDrawn, { places: x.places ?? w.noLimit, guaranteed: x.guaranteed, winners: x.winners, pool: x.pool, waiting: x.waiting });
}

/** The facts and numbers of one event's report as label / value pairs, in order (what the PDF and the copy print). */
export function eventReportFacts(r: EventReport, lang: ReportLang): [string, string][] {
  const w = REPORT_WORDS[lang];
  const out: [string, string][] = [[w.date, reportDay(r.dayKey, lang)]];
  const time = reportTime(r);
  if (time) out.push([w.time, time]);
  out.push([w.place, r.place || '—'], [w.kind, reportKind(r, lang)]);
  if (r.counts) for (const k of REPORT_COUNTS) out.push([w[k], String(r.counts[k])]);
  if (r.volunteers) out.push([w.volunteers, r.volunteers.needed === null ? String(r.volunteers.going) : fill(w.of, { going: r.volunteers.going, needed: r.volunteers.needed })]);
  if (r.raffle) out.push([w.raffle, raffleWords(r.raffle, lang)]);
  return out;
}

/** The notes under the numbers: calendar only, the hours, cancelled. */
export function eventReportNotes(r: EventReport, lang: ReportLang): string[] {
  const w = REPORT_WORDS[lang];
  return [r.cancelled ? w.cancelled : '', !r.counts ? w.calendarOnly : '', r.volunteers ? w.hoursNotKept : ''].filter(Boolean);
}

/** One event's report as plain text (Copy). */
export function eventReportText(r: EventReport, lang: ReportLang): string {
  const w = REPORT_WORDS[lang];
  return [`${w.title} — ${r.name}`, ...eventReportFacts(r, lang).map(([k, v]) => `${k}: ${v}`), ...eventReportNotes(r, lang), w.countsOnly].join('\n');
}

/** The totals as label / value pairs, in order. */
export function yearTotalsFacts(y: YearReport, lang: ReportLang): [string, string][] {
  const w = REPORT_WORDS[lang];
  return [
    [w.events, String(y.totals.events)],
    [w.eventsCancelled, String(y.totals.cancelled)],
    [w.invited, String(y.totals.invited)],
    [w.saidYes, String(y.totals.saidYes)],
    [w.came, String(y.totals.came)],
    [w.households, String(y.totals.households)],
    [w.guests, String(y.totals.guests)],
    [w.volunteers, String(y.totals.volunteers)],
  ];
}

/** A number of one row, or "—" when it did not apply. */
export const rowCount = (r: EventReport, k: ReportCount): string => (r.counts ? String(r.counts[k]) : '—');
export const rowVolunteers = (r: EventReport, lang: ReportLang): string => (!r.volunteers ? '—' : r.volunteers.needed === null ? String(r.volunteers.going) : fill(REPORT_WORDS[lang].of, { going: r.volunteers.going, needed: r.volunteers.needed }));

/** The last twelve months as plain text (Copy): the window, one line an event, the totals. */
export function yearReportText(y: YearReport, lang: ReportLang): string {
  const w = REPORT_WORDS[lang];
  const lines = y.rows.map((r) => {
    const parts = [reportDay(r.dayKey, lang), r.name, reportKind(r, lang)];
    if (r.cancelled) parts.push(w.cancelled);
    else {
      if (r.counts) parts.push(...REPORT_COUNTS.map((k) => `${w[k]} ${r.counts![k]}`));
      if (r.volunteers) parts.push(`${w.volunteers} ${rowVolunteers(r, lang)}`);
      if (r.raffle) parts.push(`${w.raffle}: ${raffleWords(r.raffle, lang)}`);
    }
    return `- ${parts.join(' · ')}`;
  });
  return [`${w.yearTitle} (${fill(w.window, { from: reportDay(y.fromKey, lang), to: reportDay(y.toKey, lang) })})`, ...(lines.length ? lines : [w.none]), `${w.totals}: ${yearTotalsFacts(y, lang).map(([k, v]) => `${k} ${v}`).join(' · ')}`, w.hoursNotKept, w.countsOnly].join('\n');
}
